import { Loader2 } from "lucide-react"
import { Slot } from "radix-ui"
import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

export const buttonVariants = {
	primary: "bg-foreground text-background hover:bg-primary",
	secondary:
		"border border-border bg-background text-foreground hover:bg-muted",
	outline:
		"border border-foreground bg-transparent text-foreground hover:border-primary hover:text-primary",
	ghost: "text-muted-foreground hover:bg-foreground/5 hover:text-primary",
	destructive:
		"border border-destructive/40 bg-destructive/10 text-destructive [&_svg]:text-destructive hover:bg-destructive/15 hover:border-destructive/60",
	tinted:
		"border border-primary/40 bg-primary/10 text-primary [&_svg]:text-primary hover:bg-primary/15 hover:border-primary/60",
	"tinted-muted":
		"border border-muted-foreground/30 bg-muted-foreground/8 text-muted-foreground dark:bg-border/70 [&_svg]:text-muted-foreground hover:bg-muted-foreground/12 hover:border-muted-foreground/50 hover:text-foreground hover:[&_svg]:text-foreground",
	"tinted-muted-primary":
		"border border-muted-foreground/30 bg-muted-foreground/8 text-muted-foreground [&_svg]:text-muted-foreground hover:bg-primary/15 hover:border-primary/60 hover:text-primary hover:[&_*]:text-primary hover:[&_svg]:text-primary",
	link: "text-primary underline-offset-4 hover:underline",
} as const

export const buttonSizes = {
	sm: "flex h-7 items-center justify-center gap-1.5 px-2.5 text-xs",
	default:
		"flex h-8 items-center justify-center gap-2 px-2.5 text-sm font-normal",
	md: "flex h-9 items-center justify-center px-4 text-sm",
	icon: "flex h-7 w-7 items-center justify-center",
} as const

type Variant = keyof typeof buttonVariants
type Size = keyof typeof buttonSizes

interface ButtonProps extends React.ComponentProps<"button"> {
	children?: ReactNode
	variant?: Variant
	size?: Size
	isLoading?: boolean
	asChild?: boolean
}

export function Button({
	children,
	variant = "primary",
	size = "sm",
	isLoading,
	asChild = false,
	disabled,
	className,
	...props
}: ButtonProps) {
	const Comp = asChild ? Slot.Root : "button"

	return (
		<Comp
			disabled={disabled || isLoading}
			className={cn(
				"cursor-pointer rounded-lg font-medium transition-colors",
				"disabled:cursor-default disabled:opacity-50",
				"[&_svg]:pointer-events-none [&_svg]:shrink-0",
				buttonVariants[variant],
				buttonSizes[size],
				className,
			)}
			{...props}
		>
			{isLoading ? <Loader2 className="size-3.5 animate-spin" /> : children}
		</Comp>
	)
}
